import { PageRequest } from '@/core/request/PageRequest';
import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  createInformative,
  deleteInformative,
  informativeDetail,
  listInformative,
} from './Informative.service';
import {
  InformativeCreateRequest,
  ListInformativeQuery,
} from './Informative.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useInformatives(
  params?: ListInformativeQuery,
  enabled?: boolean,
) {
  const query = useInfiniteQuery(
    ['informatives', { ...params, enabled }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return listInformative(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled || hasPermissions([Authorities.DEFAULT_INFORMATIVE]),
    },
  );

  useServiceError(query.error);

  return query;
}

export function useInformative(informativeId: number) {
  const query = useQuery(
    ['informative', informativeId],
    () => (informativeId ? informativeDetail(informativeId) : undefined),
    {
      enabled: !!informativeId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useDeleteInformative() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteInformative, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'informative.notifications.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'informative.notifications.remove.success.description',
        }),
      });
      queryClient.invalidateQueries(['informatives']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useInformativeCreate() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: InformativeCreateRequest) => createInformative(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'informative.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'informative.create.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
